import { logger } from '../utils/logger';
import { authService } from './authService';

export const SIGN_IN_EXPIRED_MESSAGE = 'Your sign-in expired. Please sign in again.';

/** The backend said 401 and a silent refresh did not fix it; only the user's own click can. */
export class SignInExpiredError extends Error {
  constructor() {
    super(SIGN_IN_EXPIRED_MESSAGE);
    this.name = 'SignInExpiredError';
  }
}

/**
 * Per-turn 401 recovery state. One instance per user turn, shared by the
 * classifier and the grounding call, so the turn forces at most ONE silent
 * refresh however many of its requests come back 401. Nothing here ever opens a
 * prompt: that belongs to the "Sign in again" button (signInThenResend).
 */
export class ReauthTurn {
  /** Set once a 401 could not be recovered; the turn then ends asking the user to sign in. */
  signInExpired = false;
  private refresh: Promise<string | null> | undefined;

  /** One silent forceRefresh per turn; later callers share the outcome. Never throws. */
  refreshSilently(): Promise<string | null> {
    if (!this.refresh) {
      this.refresh = authService.getAccessTokenForceRefresh().catch((error: unknown) => {
        logger.warn('Silent token refresh failed:', error);
        return null;
      });
    }
    return this.refresh;
  }
}

function bearerOf(headers: Record<string, string>): string | undefined {
  return headers.Authorization?.replace(/^Bearer /, '');
}

/**
 * Send a user-initiated request. On HTTP 401, force ONE silent token refresh and
 * retry ONCE, but only if the refreshed token differs from the one the backend
 * just rejected (a cache hit would 401 identically). The retry is the same
 * request with only Authorization replaced. A second 401, a refresh that fails or
 * times out, or an unchanged token throws SignInExpiredError. Any other status
 * (including 5xx) is returned untouched. Fire-and-forget calls must not use this.
 */
export async function fetchWithReauth(
  turn: ReauthTurn,
  route: string,
  buildHeaders: () => Promise<Record<string, string>>,
  send: (headers: Record<string, string>) => Promise<Response>
): Promise<Response> {
  const headers = await buildHeaders();
  const first = await send(headers);
  if (first.status !== 401) return first;

  logger.warn(`${route} returned 401; refreshing the token silently once`);
  const token = await turn.refreshSilently();
  if (!token || token === bearerOf(headers)) {
    turn.signInExpired = true;
    throw new SignInExpiredError();
  }
  const retry = await send({ ...headers, Authorization: `Bearer ${token}` });
  if (retry.status === 401) {
    logger.warn(`${route} returned 401 again after a token refresh`);
    turn.signInExpired = true;
    throw new SignInExpiredError();
  }
  return retry;
}

let signInInFlight: Promise<boolean> | undefined;

/**
 * The "Sign in again" button's handler. Must be called straight from the click:
 * it opens the interactive sign-in first (one prompt), then runs resend exactly
 * once. A cancelled or failed prompt resolves false without resending and leaves
 * the button usable. A second click while one is in flight shares it.
 */
export function signInThenResend(resend: () => Promise<void> | void): Promise<boolean> {
  if (signInInFlight) return signInInFlight;
  const run = (async () => {
    let token: string | null = null;
    try {
      token = await authService.acquireTokenInteractive();
    } catch (error) {
      logger.warn('Sign-in from the button did not complete:', error);
    }
    if (!token) return false;
    await resend();
    return true;
  })();
  signInInFlight = run.finally(() => {
    signInInFlight = undefined;
  });
  return signInInFlight;
}
